import React, { useState, useEffect } from 'react'
import {Send, Lock, Globe, LogIn} from 'lucide-react'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'
import PrayerWall from '../components/PrayerWall'

const Prayer: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    request: '',
    isPrivate: false,
    type: 'Família'
  })
  const [publicPrayers, setPublicPrayers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [checkingAuth, setCheckingAuth] = useState(true)

  useEffect(() => {
    fetchPublicPrayers()
    checkAuthentication()

    // Listener para mudanças de autenticação
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user: authUser }) => {
      setUser(authUser)
      setCheckingAuth(false)
    })

    return unsubscribe
  }, [])

  const checkAuthentication = async () => {
    try {
      const currentUser = lumi.auth.user
      setUser(currentUser)
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error)
    } finally {
      setCheckingAuth(false)
    }
  }

  const fetchPublicPrayers = async () => {
    try {
      setLoading(true)
      const { list } = await lumi.entities.prayers.list({
        filter: { 
          isPrivate: false, 
          status: 'APPROVED'
        },
        sort: { createdAt: -1 },
        limit: 20
      })
      setPublicPrayers(list)
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) {
      toast.error('Você precisa estar logado para enviar um pedido de oração')
      return
    }

    // Validação de campos obrigatórios
    if (!formData.name.trim() || !formData.email.trim() || !formData.request.trim()) {
      toast.error('Por favor, preencha todos os campos obrigatórios')
      return
    }

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error('Por favor, insira um email válido')
      return
    }

    // Sanitização de inputs
    const sanitizeInput = (input: string): string => {
      return input
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .trim()
    }
    
    setSubmitting(true)
    try {
      const currentUser = lumi.auth.user
      
      await lumi.entities.prayers.create({
        name: sanitizeInput(formData.name),
        email: formData.email,
        message: sanitizeInput(formData.request),
        isPrivate: formData.isPrivate,
        type: formData.type,
        status: 'PENDING',
        creator: currentUser?.userId || user.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      toast.success(
        'Pedido recebido e aguardando aprovação. Estaremos orando por você!',
        { duration: 5000 }
      )
      
      setFormData({ name: '', email: '', request: '', isPrivate: false, type: 'Família' })
      
      // Recarregar pedidos após envio
      if (!formData.isPrivate) {
        setTimeout(() => fetchPublicPrayers(), 1000)
      }
    } catch (error) {
      console.error('Erro ao enviar pedido:', error)
      toast.error('Erro ao enviar pedido. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71] py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Caixa de Oração
            </h1>
            <p className="text-xl text-white/90">
              Compartilhe seu pedido conosco. Estamos aqui para orar por você.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Formulário */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {checkingAuth ? (
              <div className="bg-gray-50 rounded-xl p-8 shadow-lg text-center">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
                </div>
                <p className="text-gray-600 mt-4">Verificando autenticação...</p>
              </div>
            ) : !user ? (
              <div className="bg-gradient-to-r from-[#1e3a8a] to-[#6b8f71] rounded-xl p-8 text-center shadow-lg">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Faça Login para Enviar seu Pedido
                </h3>
                <p className="text-white/90 mb-6">
                  É necessário estar cadastrado para enviar pedidos de oração
                </p>
                <button
                  onClick={() => lumi.auth.signIn()}
                  className="bg-white text-[#1e3a8a] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 shadow-lg inline-flex items-center gap-2"
                >
                  <LogIn size={20} />
                  Fazer Login / Cadastrar
                </button>
              </div>
            ) : (
            <div className="bg-gray-50 rounded-xl p-8 shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b8f71] focus:border-transparent outline-none transition-all"
                    placeholder="Seu nome"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b8f71] focus:border-transparent outline-none transition-all"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="type" className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Pedido
                  </label>
                  <select
                    id="type"
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b8f71] focus:border-transparent outline-none transition-all bg-white"
                  >
                    <option value="Família">Família</option>
                    <option value="Saúde">Saúde</option>
                    <option value="Cura">Cura</option>
                    <option value="Financeiro">Financeiro</option>
                    <option value="Trabalho">Trabalho</option>
                    <option value="Relacionamentos">Relacionamentos</option>
                    <option value="Espiritual">Espiritual</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="request" className="block text-sm font-semibold text-gray-700 mb-2">
                    Pedido de Oração
                  </label>
                  <textarea
                    id="request"
                    required
                    rows={6}
                    value={formData.request}
                    onChange={(e) => setFormData({ ...formData, request: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b8f71] focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Compartilhe seu pedido de oração..."
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="isPrivate"
                    checked={formData.isPrivate}
                    onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                    className="mt-1 w-4 h-4 text-[#6b8f71] border-gray-300 rounded focus:ring-[#6b8f71]"
                  />
                  <label htmlFor="isPrivate" className="text-sm text-gray-700 flex-1">
                    <span className="font-semibold flex items-center gap-2">
                      {formData.isPrivate ? (
                        <>
                          <Lock size={16} className="text-[#1e3a8a]" />
                          Pedido Sigiloso
                        </>
                      ) : (
                        <>
                          <Globe size={16} className="text-[#6b8f71]" />
                          Pedido Público
                        </>
                      )}
                    </span>
                    <span className="block mt-1 text-gray-600">
                      {formData.isPrivate 
                        ? 'Apenas a liderança verá este pedido'
                        : 'Este pedido será compartilhado com a comunidade no mural de orações'
                      }
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#6b8f71] text-white py-4 rounded-lg font-semibold hover:bg-[#5a7760] transition-all hover:scale-[1.02] flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Enviar Pedido de Oração
                    </>
                  )}
                </button>
              </form>
            </div>
            )}

            <div className="mt-8 text-center">
              <p className="text-gray-600 italic">
                "Não andeis ansiosos de coisa alguma; em tudo, porém, sejam conhecidas, diante de Deus, 
                as vossas petições, pela oração e pela súplica, com ações de graças."
                <br />
                <span className="text-[#1e3a8a] font-semibold">- Filipenses 4:6</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mural de Orações */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1e3a8a] mb-3">
                Mural de Orações
              </h2>
              <p className="text-gray-600">
                Pedidos públicos aprovados pela nossa comunidade
              </p>
            </div>

            <PrayerWall 
              prayers={publicPrayers} 
              loading={loading} 
              showAll={true}
            />
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Prayer
